import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import Swal from "sweetalert2";
import { DataGrid } from "@mui/x-data-grid";
import Popover from "@mui/material/Popover";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const UserCollegeRoleAssignment = () => {
  // ============================================================
  // SESSION
  // ============================================================

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const clientId = user.client_id;
  const currentCollegeId = user.college_id;
  const loggedUserId = Number(sessionStorage.getItem("user_id"));
  const permissionRequestRef = useRef(0);
  // ============================================================
  // LIST
  // ============================================================

  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const [search, setSearch] = useState("");

  // ============================================================
  // DIALOG
  // ============================================================

  const [openDialog, setOpenDialog] = useState(false);

  // ============================================================
  // USER SEARCH
  // ============================================================

  const [userSearch, setUserSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [loadingUsers, setLoadingUsers] = useState(false);

  // ============================================================
  // COLLEGES
  // ============================================================

  const [userColleges, setUserColleges] = useState([]);
  const [selectedCollegeId, setSelectedCollegeId] = useState("");

  const [loadingColleges, setLoadingColleges] = useState(false);

  // ============================================================
  // ROLES
  // ============================================================

  const [roles, setRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  const [loadingRoles, setLoadingRoles] = useState(false);

  // ============================================================
  // SAVE
  // ============================================================

  const [saving, setSaving] = useState(false);
  // ============================================================
  // ROLE PERMISSION PREVIEW
  // ============================================================

  const [permissionAnchorEl, setPermissionAnchorEl] = useState(null);
  const [hoveredRole, setHoveredRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  const permissionPopoverOpen = Boolean(permissionAnchorEl);
  // ============================================================
  // SNACKBAR
  // ============================================================

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // ============================================================
  // SNACKBAR HELPER
  // ============================================================

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  }, []);

  const closeSnackbar = () => {
    setSnackbar((previous) => ({
      ...previous,
      open: false,
    }));
  };
  const loadRolePermissions = async (role, anchorElement) => {
    if (!role?.role_id || !selectedCollegeId) {
      return;
    }

    setHoveredRole(role);
    setPermissionAnchorEl(anchorElement);
    setRolePermissions([]);
    setLoadingPermissions(true);

    const requestId = ++permissionRequestRef.current;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/role-permission-matrix/${clientId}/${selectedCollegeId}/${role.role_id}`
      );

      if (requestId !== permissionRequestRef.current) {
        return;
      }

      const result = response.data?.data ?? response.data ?? {};

      setRolePermissions(Array.isArray(result?.modules) ? result.modules : []);
    } catch (error) {
      if (requestId === permissionRequestRef.current) {
        console.error("Failed to load role permissions:", error);

        setRolePermissions([]);
      }
    } finally {
      if (requestId === permissionRequestRef.current) {
        setLoadingPermissions(false);
      }
    }
  };
  const handlePermissionPopoverClose = () => {
    setPermissionAnchorEl(null);
    setHoveredRole(null);
    setRolePermissions([]);
    setLoadingPermissions(false);

    permissionRequestRef.current += 1;
  };
  // ============================================================
  // LOAD GRID
  // ============================================================

  const loadAssignments = useCallback(async () => {
    if (!clientId) return;

    try {
      setLoadingRows(true);

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/user-college-roles/list/${clientId}`,
        {
          params: {
            page: page + 1,
            pageSize,
            search: search.trim(),
          },
        }
      );

      const responseData = response.data?.data ?? response.data ?? {};

      const assignmentRows =
        responseData?.rows ??
        responseData?.data ??
        (Array.isArray(responseData) ? responseData : []);

      const total = responseData?.total ?? responseData?.count ?? assignmentRows.length;

      setRows(
        assignmentRows.map((item, index) => ({
          id:
            item.user_college_role_id ??
            `${item.user_id}-${item.college_id}-${item.role_id}-${index}`,

          ...item,
        }))
      );

      setRowCount(Number(total) || 0);
    } catch (error) {
      console.error("Failed to load user college roles:", error);

      showSnackbar(
        error.response?.data?.message || "Failed to load user college role assignments.",
        "error"
      );
    } finally {
      setLoadingRows(false);
    }
  }, [clientId, page, pageSize, search, showSnackbar]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  // ============================================================
  // SEARCH USERS
  // ============================================================

  const searchUsers = async (value) => {
    setUserSearch(value);

    if (!value || !value.trim()) {
      setUsers([]);
      return;
    }

    try {
      setLoadingUsers(true);

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/user-college-roles/users/${clientId}`,
        {
          params: {
            search: value.trim(),
          },
        }
      );

      const data = response.data?.data ?? response.data ?? [];

      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to search users:", error);

      setUsers([]);

      showSnackbar(error.response?.data?.message || "Failed to search users.", "error");
    } finally {
      setLoadingUsers(false);
    }
  };

  // ============================================================
  // LOAD USER COLLEGES
  // ============================================================

  const loadUserColleges = async (userId) => {
    if (!userId) return;

    try {
      setLoadingColleges(true);

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/user-college-roles/colleges/${clientId}/${userId}`
      );

      const data = response.data?.data ?? response.data ?? [];

      setUserColleges(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load user colleges:", error);

      setUserColleges([]);

      showSnackbar(
        error.response?.data?.message || "Failed to load colleges assigned to user.",
        "error"
      );
    } finally {
      setLoadingColleges(false);
    }
  };

  // ============================================================
  // USER SELECT
  // ============================================================

  const handleUserSelect = async (event, newValue) => {
    setSelectedUser(newValue);

    setSelectedCollegeId("");
    setUserColleges([]);

    setRoles([]);
    setSelectedRoleIds([]);

    if (!newValue?.user_id) {
      return;
    }

    await loadUserColleges(newValue.user_id);
  };

  // ============================================================
  // LOAD ROLES
  // ============================================================

  const loadRolesForCollege = async (collegeId) => {
    if (!collegeId || !selectedUser?.user_id) {
      return;
    }

    try {
      setLoadingRoles(true);

      const [rolesResponse, assignedResponse] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/api/rbacall/user-college-roles/roles/${clientId}/${selectedUser.user_id}/${collegeId}`
        ),

        axios.get(
          `${API_BASE_URL}/api/rbacall/user-college-roles/assigned/${clientId}/${selectedUser.user_id}/${collegeId}`
        ),
      ]);

      const availableRoles = rolesResponse.data?.data ?? rolesResponse.data ?? [];

      const assignedRoles = assignedResponse.data?.data ?? assignedResponse.data ?? [];

      setRoles(Array.isArray(availableRoles) ? availableRoles : []);

      const assignedIds = Array.isArray(assignedRoles)
        ? assignedRoles
            .map((item) => {
              if (typeof item === "object") {
                return Number(item.role_id);
              }

              return Number(item);
            })
            .filter((item) => !Number.isNaN(item))
        : [];

      setSelectedRoleIds(assignedIds);
    } catch (error) {
      console.error("Failed to load roles:", error);

      setRoles([]);
      setSelectedRoleIds([]);

      showSnackbar(
        error.response?.data?.message || "Failed to load roles for selected college.",
        "error"
      );
    } finally {
      setLoadingRoles(false);
    }
  };

  // ============================================================
  // COLLEGE CHANGE
  // ============================================================

  const handleCollegeChange = async (event) => {
    const collegeId = event.target.value;

    setSelectedCollegeId(collegeId);

    setRoles([]);
    setSelectedRoleIds([]);

    if (!collegeId || !selectedUser?.user_id) {
      return;
    }

    await loadRolesForCollege(collegeId);
  };

  // ============================================================
  // ROLE CHECKBOX
  // ============================================================

  const handleRoleChange = (roleId) => {
    const id = Number(roleId);

    if (!Number.isFinite(id)) {
      return;
    }

    // IMPORTANT:
    // Selecting a role must never leave/open the permission popover.
    handlePermissionPopoverClose();

    setSelectedRoleIds((previous) => {
      const current = previous.map(Number);

      if (current.includes(id)) {
        return current.filter((item) => item !== id);
      }

      return [...current, id];
    });
  };

  // ============================================================
  // SELECT ALL ROLES
  // ============================================================

  const allRoleIds = useMemo(() => {
    return roles.map((role) => Number(role.role_id)).filter((id) => !Number.isNaN(id));
  }, [roles]);

  const allRolesSelected =
    allRoleIds.length > 0 && allRoleIds.every((roleId) => selectedRoleIds.includes(roleId));

  const someRolesSelected = selectedRoleIds.length > 0 && !allRolesSelected;

  const handleSelectAllRoles = (event) => {
    if (event.target.checked) {
      setSelectedRoleIds(allRoleIds);
    } else {
      setSelectedRoleIds([]);
    }
  };

  // ============================================================
  // OPEN ADD / ASSIGN DIALOG
  // ============================================================

  const handleOpenDialog = () => {
    setUserSearch("");
    setUsers([]);

    setSelectedUser(null);

    setUserColleges([]);
    setSelectedCollegeId("");

    setRoles([]);
    setSelectedRoleIds([]);

    setOpenDialog(true);
  };

  // ============================================================
  // CLOSE DIALOG
  // ============================================================

  const handleCloseDialog = () => {
    if (saving) return;

    setOpenDialog(false);

    setUserSearch("");
    setUsers([]);

    setSelectedUser(null);

    setUserColleges([]);
    setSelectedCollegeId("");

    setRoles([]);
    setSelectedRoleIds([]);
  };

  // ============================================================
  // SAVE
  // ============================================================

  const handleSave = async () => {
    if (!selectedUser?.user_id) {
      showSnackbar("Please select a user.", "warning");
      return;
    }

    if (!selectedCollegeId) {
      showSnackbar("Please select a college.", "warning");
      return;
    }

    try {
      setSaving(true);

      const response = await axios.put(
        `${API_BASE_URL}/api/rbacall/user-college-roles/${clientId}/${selectedUser.user_id}/${selectedCollegeId}`,
        {
          role_ids: selectedRoleIds,
          modified_by: loggedUserId || null,
        }
      );

      showSnackbar(response.data?.message || "User college roles saved successfully.", "success");

      // Refresh the DataGrid
      await loadAssignments();

      // IMPORTANT:
      // Keep dialog open.
      // Reload roles for the currently selected college
      // so the checkboxes exactly match the database.
      await loadRolesForCollege(selectedCollegeId);

      // DO NOT call:
      // handleCloseDialog();
    } catch (error) {
      console.error("Failed to save roles:", error);

      showSnackbar(error.response?.data?.message || "Failed to save user college roles.", "error");
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // DELETE ONE ROLE ASSIGNMENT
  // ============================================================

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Remove Role?",
      text: `Remove role "${row.role_name || ""}" from this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#757575",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/rbacall/user-college-roles/${clientId}/${row.user_id}/${row.college_id}/${row.role_id}`
      );

      showSnackbar(`Role " ${row.role_name || ""} " removed successfully.`, "success");

      await loadAssignments();
    } catch (error) {
      console.error("Failed to delete role:", error);

      showSnackbar(error.response?.data?.message || "Failed to remove role.", "error");
    }
  };

  // ============================================================
  // GRID COLUMNS
  // ============================================================

  const columns = useMemo(
    () => [
      {
        field: "serial_no",
        headerName: "S.No",
        width: 75,
        sortable: false,
        renderCell: (params) =>
          page * pageSize + params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },

      {
        field: "user_name",
        headerName: "User Name",
        minWidth: 180,
        flex: 1,
      },

      {
        field: "user_email",
        headerName: "Email",
        minWidth: 230,
        flex: 1.2,
      },

      {
        field: "college_id",
        headerName: "College Id",
        minWidth: 220,
        flex: 1,
      },

      {
        field: "role_name",
        headerName: "Role",
        minWidth: 180,
        flex: 0.8,
      },

      {
        field: "role_code",
        headerName: "Role Code",
        minWidth: 140,
        flex: 0.6,
      },

      {
        field: "is_active",
        headerName: "Status",
        width: 110,
        renderCell: (params) => {
          const active = params.value === true || params.value === 1 || params.value === "1";

          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: active ? "success.main" : "error.main",
              }}
            >
              {active ? "Active" : "Inactive"}
            </Typography>
          );
        },
      },

      {
        field: "actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            size="small"
            variant="contained"
            onClick={() => handleDelete(params.row)}
            sx={{
              backgroundColor: "#C62828",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#8E0000",
              },
            }}
          >
            Remove
          </Button>
        ),
      },
    ],
    [page, pageSize, handleDelete]
  );

  // ============================================================
  // RETURN
  // ============================================================

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
      {/* ========================================================
          HEADER
      ======================================================== */}

      <Paper
        elevation={2}
        sx={{
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1,
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              flexGrow: 1,
              minWidth: 200,
            }}
          >
            ASSIGN ROLES TO USER
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <TextField
              size="small"
              placeholder="Search email / Name"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 240 },
              }}
            />

            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadAssignments}
              disabled={loadingRows}
              sx={{
                width: { xs: "100%", sm: "auto", color: "#e4e8eb" },
              }}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{
                width: { xs: "100%", sm: "auto", color: "#fff" },
              }}
            >
              Assign Roles
            </Button>
          </Stack>
        </Toolbar>

        <Divider />

        {/* ======================================================
            DATA GRID
        ====================================================== */}

        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loadingRows}
            pagination
            paginationMode="server"
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            autoHeight
            disableSelectionOnClick
            sx={{
              border: 0,

              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                fontWeight: 600,
              },

              "& .MuiDataGrid-cell": {
                fontSize: "0.9rem",
              },
            }}
          />
        </Box>
      </Paper>

      {/* ========================================================
          ASSIGN ROLE DIALOG
      ======================================================== */}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 800,
            mx: { xs: 1, sm: "auto" },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            pb: 1,
          }}
        >
          Assign College wise Roles to User
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {/* ==================================================
                USER SEARCH
            ================================================== */}

            <Autocomplete
              fullWidth
              options={users}
              value={selectedUser}
              loading={loadingUsers}
              filterOptions={(options) => options}
              getOptionLabel={(option) => option?.user_email || ""}
              isOptionEqualToValue={(option, value) =>
                Number(option.user_id) === Number(value.user_id)
              }
              onInputChange={(event, value, reason) => {
                if (reason === "input") {
                  searchUsers(value);
                }
              }}
              onChange={handleUserSelect}
              noOptionsText={
                userSearch ? "No users with assigned colleges found" : "Type email to search"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search User"
                  placeholder="Type email"
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* ==================================================
                SELECTED USER INFORMATION
            ================================================== */}

            {selectedUser && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  backgroundColor: "#fafafa",
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: 3 }}>
                  <Typography variant="body2">
                    <strong>Name:</strong> {selectedUser.user_name || "-"}
                  </Typography>

                  <Typography variant="body2">
                    <strong>Email:</strong> {selectedUser.user_email || "-"}
                  </Typography>

                  {selectedUser.user_mobile && (
                    <Typography variant="body2">
                      <strong>Mobile:</strong> {selectedUser.user_mobile}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}

            {/* ==================================================
                COLLEGE
            ================================================== */}

            <FormControl
              fullWidth
              disabled={!selectedUser || loadingColleges || userColleges.length === 0}
            >
              <InputLabel shrink>College</InputLabel>

              <TextField
                select
                label={`College count:(${userColleges.length}) allotted`}
                value={selectedCollegeId}
                onChange={handleCollegeChange}
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  native: true,
                }}
                disabled={!selectedUser || loadingColleges || userColleges.length === 0}
              >
                <option value="">
                  {loadingColleges
                    ? "Loading colleges..."
                    : !selectedUser
                    ? "Select user first"
                    : userColleges.length === 0
                    ? "No colleges assigned"
                    : "Select college"}
                </option>

                {userColleges.map((college) => (
                  <option key={college.college_id} value={college.college_id}>
                    {college.college_name || `College ${college.college_id}`}
                  </option>
                ))}
              </TextField>

              {selectedUser && userColleges.length === 0 && !loadingColleges && (
                <FormHelperText error>This user has no assigned colleges.</FormHelperText>
              )}
            </FormControl>

            {/* ==================================================
                ROLES
            ================================================== */}

            {selectedCollegeId && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  width: "100%",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Available Roles
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Select one or more roles for this user. click on permissions to view its
                      permissions.
                    </Typography>
                  </Box>

                  {roles.length > 0 && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allRolesSelected}
                          indeterminate={someRolesSelected}
                          onChange={handleSelectAllRoles}
                        />
                      }
                      label="Select All"
                    />
                  )}
                </Stack>

                <Divider sx={{ mb: 1 }} />

                {loadingRoles ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 3,
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : roles.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No roles available.
                  </Typography>
                ) : (
                  <FormGroup>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                        },
                        gap: 0.5,
                      }}
                    >
                      {roles.map((role) => {
                        const roleId = Number(role.role_id);
                        const isSelected = selectedRoleIds.includes(roleId);

                        return (
                          <Box
                            key={`role-${roleId}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              minHeight: 52,
                              border: "1px solid",
                              borderColor: isSelected ? "primary.main" : "divider",
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                              mb: 0.75,
                              backgroundColor: isSelected ? "action.selected" : "background.paper",
                              cursor: "pointer",
                              userSelect: "none",

                              "&:hover": {
                                backgroundColor: "action.hover",
                                borderColor: "primary.main",
                              },
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              onChange={(event) => {
                                event.stopPropagation();
                                handleRoleChange(roleId);
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                              size="small"
                            />

                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                cursor: "pointer",
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleRoleChange(roleId);
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {role.role_name}
                              </Typography>

                              {role.role_code && (
                                <Typography variant="caption" color="text.secondary">
                                  {role.role_code}
                                </Typography>
                              )}
                            </Box>
                            {/* Permission preview icon */}
                            <Button
                              size="small"
                              variant="text"
                              onClick={(event) => {
                                event.stopPropagation();

                                loadRolePermissions(role, event.currentTarget);
                              }}
                              sx={{
                                minWidth: "auto",
                                px: 1,
                                py: 0.3,
                                fontSize: 12,
                                textTransform: "none",
                              }}
                            >
                              Permissions
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  </FormGroup>
                )}

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 1.5,
                  }}
                >
                  Selected roles: {selectedRoleIds.length}
                </Typography>
              </Paper>
            )}
          </Stack>
        </DialogContent>

        {/* ======================================================
            DIALOG ACTIONS
        ====================================================== */}

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            pb: 2,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              width: { xs: "100%", sm: "auto", color: "#e91515" },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !selectedUser || !selectedCollegeId || loadingRoles}
            sx={{
              width: { xs: "100%", sm: "auto", color: "#fff" },
            }}
          >
            {saving ? "Saving..." : "Save Roles"}
          </Button>
        </DialogActions>
      </Dialog>
      <Popover
        open={permissionPopoverOpen}
        anchorEl={permissionAnchorEl}
        onClose={handlePermissionPopoverClose}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: {
              xs: "90vw",
              sm: 520,
            },
            maxWidth: 520,
            maxHeight: 600,
            overflow: "auto",
            p: 2,
            backgroundColor: "background.paper",
            boxShadow: 8,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            ml: 0.5,
            zIndex: (theme) => theme.zIndex.modal + 10,
          },
        }}
      >
        {/* Header */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
              }}
            >
              {hoveredRole?.role_name || "Role Permissions"}
            </Typography>

            {hoveredRole?.role_code && (
              <Typography variant="caption" color="text.secondary">
                {hoveredRole.role_code}
              </Typography>
            )}
          </Box>

          <Button
            size="small"
            onClick={handlePermissionPopoverClose}
            sx={{
              minWidth: "auto",
              fontSize: 12,
              textTransform: "none",
            }}
          >
            Close
          </Button>
        </Box>

        {loadingPermissions ? (
          <Box
            sx={{
              py: 5,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : rolePermissions.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              py: 3,
              textAlign: "center",
            }}
          >
            No permissions assigned to this role.
          </Typography>
        ) : (
          <Box>
            {rolePermissions.map((module) => (
              <Box
                key={`module-${module.module_id}`}
                sx={{
                  mb: 1.5,
                }}
              >
                {/* MODULE */}

                {/* MODULE */}
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    backgroundColor: "#E3F2FD",
                    border: "1px solid #90CAF9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: "#1565C0",
                    }}
                  >
                    {module.module_name}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: module.is_allowed ? "#2E7D32" : "#757575",
                    }}
                  >
                    {module.is_allowed ? "Allowed" : "Not Allowed"}
                  </Typography>
                </Box>

                {module.is_allowed &&
                  Array.isArray(module.tabs) &&
                  module.tabs
                    .filter((tab) => tab.is_allowed)
                    .map((tab) => (
                      <Box
                        key={`tab-${tab.tab_id}`}
                        sx={{
                          ml: 1.5,
                          mt: 1,
                          pl: 1.5,
                          borderLeft: "2px solid",
                          borderColor: "divider",
                        }}
                      >
                        {/* TAB */}

                        <Box
                          sx={{
                            px: 1.2,
                            py: 0.7,
                            borderRadius: 1,
                            backgroundColor: "#F3E5F5",
                            border: "1px solid #CE93D8",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {tab.tab_name}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="success.main"
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            Allowed
                          </Typography>
                        </Box>

                        {/* BUTTONS */}

                        {Array.isArray(tab.buttons) &&
                          tab.buttons
                            .filter((button) => button.is_allowed)
                            .map((button) => (
                              <Box
                                key={`button-${button.button_id}`}
                                sx={{
                                  px: 1.2,
                                  py: 0.6,
                                  borderRadius: 1,
                                  backgroundColor: "#FFF3E0",
                                  border: "1px solid #FFCC80",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                  }}
                                >
                                  {button.button_name}
                                </Typography>

                                {/* ACTIONS */}

                                {Array.isArray(button.actions) &&
                                  button.actions
                                    .filter((action) => action.is_allowed)
                                    .map((action) => (
                                      <Box
                                        key={`action-${action.action_id}`}
                                        sx={{
                                          ml: 2,
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Typography variant="caption" color="text.secondary">
                                          {action.action_name}

                                          {action.action_code ? ` (${action.action_code})` : ""}
                                        </Typography>

                                        <Typography
                                          variant="caption"
                                          color="success.main"
                                          sx={{
                                            fontWeight: 600,
                                          }}
                                        >
                                          Allowed
                                        </Typography>
                                      </Box>
                                    ))}
                              </Box>
                            ))}
                      </Box>
                    ))}
              </Box>
            ))}
          </Box>
        )}
      </Popover>
      {/* ========================================================
          SNACKBAR
      ======================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={closeSnackbar}
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserCollegeRoleAssignment;
